import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecipes, CATEGORIES } from '../../services/recipeService';
import { getMyGroup } from '../../services/groupService';
import { getPdfExportStatus, downloadCookbookPdf } from '../../services/cookbookPdfService';
import RecipeCard from './RecipeCard';

const RecipeGrid = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [canAddRecipes, setCanAddRecipes] = useState(true);
  const [pdfExportEnabled, setPdfExportEnabled] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const data = await getMyGroup();
        const sub = data.subscription;
        setCanAddRecipes(
          sub?.status === 'active' ||
          (sub?.status === 'trial' && !!sub?.trial_ends_at && new Date(sub.trial_ends_at) > new Date())
        );
      } catch (err) {
        setCanAddRecipes(false);
      }
    };
    checkSubscription();

    const checkPdfExport = async () => {
      try {
        const data = await getPdfExportStatus();
        setPdfExportEnabled(data.enabled);
      } catch (err) {
        setPdfExportEnabled(false);
      }
    };
    checkPdfExport();
  }, []);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const blob = await downloadCookbookPdf();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'cookbook.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const fetchRecipes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getRecipes({ search, category });
      setRecipes(data.recipes);
      setTotal(data.total);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load recipes');
    } finally {
      setLoading(false);
    }
  }, [search, category]);

  useEffect(() => {
    const timeout = setTimeout(fetchRecipes, 300); // debounce search typing
    return () => clearTimeout(timeout);
  }, [fetchRecipes]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>📖 Recipes {total > 0 && `(${total})`}</h2>
        <div style={styles.headerActions}>
          {pdfExportEnabled && (
            <button onClick={handleDownloadPdf} style={styles.pdfButton} disabled={downloadingPdf}>
              {downloadingPdf ? 'Generating...' : '📕 Print Cookbook (PDF)'}
            </button>
          )}
          {canAddRecipes ? (
            <button onClick={() => navigate('/recipes/new')} style={styles.addButton}>
              + Add Recipe
            </button>
          ) : (
            <button onClick={() => navigate('/dashboard')} style={styles.upgradeButton}>
              🔒 Purchase a Plan to Add Recipes
            </button>
          )}
        </div>
      </div>

      <div style={styles.filters}>
        <input
          type="text"
          placeholder="Search recipes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={styles.select}
        >
          <option value="">All Categories</option>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {loading && <p style={styles.status}>Loading recipes...</p>}
      {error && <p style={styles.error}>{error}</p>}

      {!loading && !error && recipes.length === 0 && (
        <div style={styles.empty}>
          <p style={styles.status}>
            {search || category ? 'No recipes match your search.' : "No recipes yet — add your first one!"}
          </p>
        </div>
      )}

      {!loading && recipes.length > 0 && (
        <div style={styles.grid}>
          {recipes.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} />
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {},
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    color: '#333',
    margin: 0,
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#e67e22',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  headerActions: {
    display: 'flex',
    gap: '10px',
  },
  pdfButton: {
    padding: '10px 20px',
    backgroundColor: 'white',
    color: '#e67e22',
    border: '1px solid #e67e22',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  upgradeButton: {
    padding: '10px 20px',
    backgroundColor: '#fdf0e3',
    color: '#e67e22',
    border: '1px solid #e67e22',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '13px',
  },
  filters: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
  },
  searchInput: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
  },
  select: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    backgroundColor: 'white',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '20px',
  },
  status: {
    color: '#999',
    textAlign: 'center',
  },
  error: {
    color: '#e74c3c',
    textAlign: 'center',
  },
  empty: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '12px',
    textAlign: 'center',
  },
};

export default RecipeGrid;
